/**
 * The recorder: WebRTC Encoded Transform first, a Web Audio tap only if we must.
 *
 * WHY ENCODED TRANSFORM IS THE PRIMARY PATH AND NOT AN OPTIMISATION.
 * `RTCRtpScriptTransform` hands us the exact Opus packets that crossed the
 * network, on both directions, with their 48 kHz RTP timestamps, at zero encode
 * cost. Four properties follow, and no other browser API has all four:
 *
 *   • BIT-EXACT. No decode, no re-encode. The user track is what the model
 *     actually heard (post-AEC, post-encode) and the assistant track is what
 *     OpenAI actually sent — pre-jitter-buffer, pre-concealment. Evidentially
 *     these are the right two things, and they are not the same as "what this
 *     laptop played out of its speakers".
 *   • FREE. A ~40-120 byte memcpy per packet. MediaRecorder and WebCodecs both
 *     re-encode, on the main thread, during a live call.
 *   • CHUNK-INDEPENDENT. Every Opus packet is self-contained, so losing a middle
 *     chunk costs that chunk. MediaRecorder puts the container header in the
 *     first blob only, which makes a truncated recording CORRUPT rather than
 *     SHORT — and a corrupt file cannot be measured, so it cannot be honestly
 *     labelled.
 *   • EXACTLY TIMED. `(rtp - rtp0) / 48000` is media seconds INCLUDING silence.
 *
 * THE RISK IT CARRIES, STATED PLAINLY: this code is in the media path. If the
 * transform stalls, the CALL stalls. The mitigation is structural rather than
 * defensive — see workers/xform.ts, which does nothing but copy and hand off,
 * and workers/sink.ts, which owns every blocking thing there is.
 *
 * WHAT THIS FILE ITSELF DOES: spawn the two workers, join them with a
 * MessageChannel so audio never crosses the UI thread, install the transforms,
 * and relay acks down / counters up. It holds no audio.
 */
import type {
  CaptureBatch,
  RecorderCounters,
  RecorderPath,
  ScriptTransformCtor,
  SinkIn,
  SinkOut,
  TrackName,
  XformIn,
} from './types'
import { emptyCounters } from './types'
import { recordingErrorName, type RecordingDiagnosticCode } from '@voiceai/wire'
import { FallbackCapture } from './fallback'
// `?worker&inline`: the worker scripts are base64-embedded in the bundle and
// instantiated from a blob at runtime — see the argument on `start()`.
import SinkWorker from './workers/sink.ts?worker&inline'
import XformWorker from './workers/xform.ts?worker&inline'

/** Target media milliseconds per chunk. */
const CHUNK_MS = 2000
/** Encoded packets per postMessage out of the media path. 8 => 160 ms. */
const BATCH_FRAMES = 8
/** How long `stop()` waits for the sink to finish flushing before giving up. */
const STOP_BUDGET_MS = 9000

export interface RecorderHooks {
  onDiagnostic?(code: RecordingDiagnosticCode, errorName?: string): void
  onCounters(counters: RecorderCounters): void
  onGap(track: TrackName, fromSeq: number, toSeq: number, reason: string): void
  /**
   * The client's own account of what it produced for a track.
   *
   * `voicedMs` is null on a track this build cannot measure it for — the caller's
   * track goes through the sender transform and never touches the tap — and null
   * must reach the row as "not measured", never as zero.
   */
  onDone(
    track: TrackName,
    lastSeq: number,
    totalPackets: number,
    voicedMs: number | null,
    provenance: 'wire' | 'rendition' | 'none',
  ): void
  onAssistantBurst(rtp: string, epochMs: number): void
  onLog(level: 'info' | 'warn' | 'error', message: string): void
}

export interface RecorderOptions {
  sessionId: string
  /**
   * Which encoder the ASSISTANT track falls back to on the encoded-transform path.
   *
   * Needed because the assistant cannot use the transform at all — see the note on
   * `attachRemote` — so the hybrid has to know what the browser can encode with,
   * which `recorderPath` no longer tells us once it says 'encoded-transform'.
   * `null` means neither is available and the assistant track cannot be captured.
   */
  assistantFallback: 'webcodecs-opus' | 'pcm16' | null
  /** Absolute /media URL with the first-connect token already applied. */
  mediaUrl: string
  /** The same URL with the long-lived reconnect token. Used on every re-dial. */
  mediaReconnectUrl: string
  path: RecorderPath
  /** Only needed by the fallback path, and only if it runs. */
  ctx: AudioContext | null
  hooks: RecorderHooks
}

export class Recorder {
  private sink: Worker | null = null
  private xform: Worker | null = null
  private fallback: FallbackCapture | null = null
  private stopping: Promise<void> | null = null
  private opened = false

  counters: RecorderCounters = emptyCounters()

  constructor(private readonly opts: RecorderOptions) {}

  get path(): RecorderPath {
    return this.opts.path
  }

  /**
   * Spawn the workers and join them. Called from the user gesture, before the
   * peer connection exists, so that the transform worker is already evaluating
   * its script by the time a transform is installed on it.
   *
   * INLINE WORKERS, NOT URL WORKERS, and it is load-bearing twice over
   * (2026-08-23, proven by embed session 2abf0e0f which recorded nothing):
   *
   *  - THE EMBED. On a customer's page the widget is a cross-origin script,
   *    and `new Worker(url)` refuses a cross-origin URL outright — a
   *    SecurityError no CORS header can fix. A worker built from an inline
   *    blob inherits the page's own origin and just runs.
   *  - THE PANEL. A URL worker is fetched at CLICK time, so a tab left open
   *    across a deploy asked for asset hashes that no longer existed and
   *    silently lost its recorder (session 3addff47). Inlined, the tab holds
   *    everything from page load.
   */
  start(): void {
    if (this.opts.path === 'none') return
    const sink = new SinkWorker({ name: 'voiceai-sink' })
    this.sink = sink
    sink.onmessage = (ev: MessageEvent) => this.onSinkMessage(ev.data as SinkOut)
    sink.onerror = (ev) => {
      this.opts.hooks.onDiagnostic?.('sink.worker_failed', recordingErrorName(ev.error))
      this.opts.hooks.onLog('error', `recorder sink worker failed: ${ev.message}`)
    }

    if (this.opts.path === 'encoded-transform') {
      // The ASSISTANT half of the hybrid. Chromium accepts a receiver-side
      // transform and never feeds it, so the only way to capture the assistant on
      // this path is the Web Audio tap — the same one the non-transform paths use
      // for both tracks. Built here so the worklet module is loaded before the
      // remote track arrives.
      if (this.opts.assistantFallback !== null && this.opts.ctx) {
        this.fallback = new FallbackCapture({
          ctx: this.opts.ctx,
          path: this.opts.assistantFallback,
          onBatch: (batch, transfer) => this.postToSink(batch, transfer),
          onLog: this.opts.hooks.onLog,
        })
        // Start loading the worklet module NOW, while we are still inside the user
        // gesture. On this path nothing else touches the AudioContext until the
        // remote track arrives in `ontrack`, which is outside the gesture, and a
        // context with nothing connected to it can be auto-suspended before the
        // first tap exists. `attach` awaits the same cached promise.
        void this.fallback.prepare().catch((e: unknown) => {
          this.opts.hooks.onDiagnostic?.('assistant.prepare_failed', recordingErrorName(e))
          this.opts.hooks.onLog('warn', `the assistant tap could not be prepared: ${String(e)}`)
        })
      }
      const xform = new XformWorker({ name: 'voiceai-xform' })
      this.xform = xform
      xform.onerror = (ev) => {
        this.opts.hooks.onDiagnostic?.('transform.worker_failed', recordingErrorName(ev.error))
        // A transform worker that throws is the one failure that could touch the
        // call, so it is loud. The frames keep flowing regardless: an errored
        // worker stops transforming, and the browser passes packets through.
        this.opts.hooks.onLog('error', `transform worker failed: ${ev.message}`)
      }
      const channel = new MessageChannel()
      xform.postMessage({ t: 'init', port: channel.port1, batchFrames: BATCH_FRAMES }, [channel.port1])
      this.postToSink(
        {
          t: 'init',
          sessionId: this.opts.sessionId,
          mediaUrl: this.opts.mediaUrl,
          mediaReconnectUrl: this.opts.mediaReconnectUrl,
          chunkMs: CHUNK_MS,
          port: channel.port2,
        },
        [channel.port2],
      )
    } else {
      this.postToSink({
        t: 'init',
        sessionId: this.opts.sessionId,
        mediaUrl: this.opts.mediaUrl,
        mediaReconnectUrl: this.opts.mediaReconnectUrl,
        chunkMs: CHUNK_MS,
        port: null,
      })
      this.fallback = new FallbackCapture({
        // The context is required on this path; `attachLocal` throws a readable
        // error rather than letting a null slip into the graph.
        ctx: this.opts.ctx as AudioContext,
        path: this.opts.path === 'webcodecs-opus' ? 'webcodecs-opus' : 'pcm16',
        onBatch: (batch, transfer) => this.postToSink(batch, transfer),
        onLog: this.opts.hooks.onLog,
      })
    }
  }

  async attachLocal(sender: RTCRtpSender, mic: MediaStream): Promise<void> {
    if (this.opts.path === 'none') return
    if (this.opts.path === 'encoded-transform') {
      this.installTransform(sender, 'user', 'sender')
      return
    }
    if (!this.opts.ctx) throw new Error('the fallback recorder needs an AudioContext')
    await this.fallback?.attach('user', mic)
  }

  /**
   * Capture the assistant.
   *
   * THE RECEIVER TRANSFORM DOES NOT WORK, AND THIS IS NOT OUR BUG. Chromium 148
   * accepts `receiver.transform = new RTCRtpScriptTransform(...)` and then never
   * delivers a frame to it. Verified on real calls and by experiment — a dedicated
   * worker for the receiver got zero frames too, so it is not the shared worker
   * that MDN documents as the intended pattern. The reasoning that settles it: the
   * assistant was audible throughout, so the frames took the normal path; frames
   * fed to a transform that swallowed them would have produced silence. Note that
   * `onrtctransform` fires when the transform is CONSTRUCTED, so its firing is not
   * evidence of attachment. See docs/next-tasks.md §4b.
   *
   * So the assistant goes through the Web Audio tap on EVERY path, and the sender
   * transform still carries the user track wire-exact where it is available. That
   * asymmetry is real and must not be papered over: what this produces for the
   * assistant is a faithful RENDITION of what this connection played, jitter
   * buffer and packet-loss concealment included — not the bytes that crossed the
   * network. `Recorder.provenance` reports it per track so nothing downstream has
   * to guess, and the call screen says so.
   */
  async attachRemote(receiver: RTCRtpReceiver, stream: MediaStream): Promise<void> {
    if (this.opts.path === 'none') return
    if (!this.fallback) {
      this.opts.hooks.onDiagnostic?.('assistant.capture_failed')
      this.opts.hooks.onLog(
        'error',
        'the assistant track cannot be captured in this browser: it needs either a '
          + 'WebCodecs Opus encoder or an AudioContext, and neither is available. '
          + 'The conversation is unaffected; only the assistant half of the recording is.',
      )
      return
    }
    await this.fallback.attach('assistant', stream)
  }

  /**
   * How each track was captured, so a claim about the artefact can be made per
   * track instead of per session. `wire` means the bytes that crossed the network.
   */
  get provenance(): Record<TrackName, 'wire' | 'rendition' | 'none'> {
    const user = this.opts.path === 'encoded-transform'
      ? 'wire'
      : this.opts.path === 'none'
        ? 'none'
        : 'rendition'
    return { user, assistant: this.fallback ? 'rendition' : 'none' }
  }

  /**
   * Install the transform on a sender or receiver.
   *
   * The cast is deliberate: `RTCRtpSender.transform` is not in TypeScript's DOM
   * lib at this version, and a `declare global` merge would break the day it
   * lands with a slightly different type. See types.ts.
   */
  private installTransform(target: RTCRtpSender | RTCRtpReceiver, track: TrackName, side: 'sender' | 'receiver'): void {
    const worker = this.xform
    if (!worker) return
    const Ctor = (globalThis as unknown as { RTCRtpScriptTransform?: ScriptTransformCtor }).RTCRtpScriptTransform
    if (!Ctor) {
      this.opts.hooks.onLog('warn', 'RTCRtpScriptTransform disappeared between the probe and the call')
      return
    }
    try {
      ;(target as unknown as { transform: unknown }).transform = new Ctor(worker, { track, side })
    } catch (e) {
      // Failing to record must never fail the call.
      this.opts.hooks.onDiagnostic?.(track === 'user' ? 'user.capture_failed' : 'assistant.capture_failed', recordingErrorName(e))
      this.opts.hooks.onLog('error', `could not attach the ${track} transform: ${String(e)}`)
    }
  }

  /** Control said `ready`. `ackSeq` is the server's authority on what it has. */
  open(ackSeq: Record<TrackName, number>): void {
    this.opened = true
    this.postToSink({ t: 'open', ackSeq })
  }

  get isOpen(): boolean {
    return this.opened
  }

  ack(track: TrackName, seq: number): void {
    this.postToSink({ t: 'ack', track, seq })
  }

  private postToSink(msg: SinkIn | CaptureBatch, transfer?: Transferable[]): void {
    const sink = this.sink
    if (!sink) return
    if (transfer) sink.postMessage(msg, transfer)
    else sink.postMessage(msg)
  }

  private onSinkMessage(msg: SinkOut): void {
    switch (msg.t) {
      case 'diagnostic':
        this.opts.hooks.onDiagnostic?.(msg.code, msg.errorName)
        break
      case 'counters':
        this.counters = msg.counters
        this.opts.hooks.onCounters(msg.counters)
        break
      case 'gap':
        this.opts.hooks.onGap(msg.track, msg.fromSeq, msg.toSeq, msg.reason)
        break
      case 'done':
        // The sink knows the counts; only this side knows how the track was
        // captured and what the tap measured. Merged here rather than plumbed
        // through the worker, which has no business knowing either.
        this.opts.hooks.onDone(
          msg.track,
          msg.lastSeq,
          msg.totalPackets,
          this.fallback?.voicedMs(msg.track) ?? null,
          this.provenance[msg.track],
        )
        break
      case 'assistant-burst':
        this.opts.hooks.onAssistantBurst(msg.rtp, msg.epochMs)
        break
      case 'log':
        this.opts.hooks.onLog(msg.level, msg.message)
        break
      case 'stopped':
        this.resolveStop?.()
        break
    }
  }

  private resolveStop: (() => void) | null = null

  /**
   * Flush and shut down.
   *
   * The budget exists because this runs while a human is waiting for the page to
   * say "ended". If the sink cannot land its backlog in nine seconds the DO will
   * finalise with what it has and mark the recording PARTIAL with a measured
   * gap, which is the honest outcome — far better than a hung button. Nine
   * seconds covers the sink's own two waits: up to 500 ms to drain the batch port
   * and up to six seconds for the uploader's final acknowledged drain.
   *
   * THE CALLER TREATS THIS RESOLVING AS "the client has finished producing and
   * shipping bytes", which is why it is awaited before the server is told to
   * finalise. See CallSession.end().
   */
  async stop(): Promise<void> {
    if (this.stopping) return this.stopping
    this.stopping = (async () => {
      // The fallback taps must flush their encoders BEFORE the sink is told to
      // stop, or the last second of audio is still inside the encoder.
      if (this.fallback) {
        try {
          await this.fallback.stop()
        } catch {
          /* nothing to salvage from a broken tap */
        }
      }
      // The transform worker is asked to stop FIRST, and it is asked at all so
      // that it flushes its partial batch and then posts the `eos` sentinel the
      // sink waits for. Without that handshake the sink's final flush races the
      // last batch of each track across two unordered message queues, and the
      // loser was silently discarded. See workers/xform.ts and types.ts.
      const xformStop: XformIn = { t: 'stop' }
      this.xform?.postMessage(xformStop)
      if (this.sink) {
        const finished = new Promise<void>((resolve) => {
          this.resolveStop = resolve
        })
        this.postToSink({ t: 'stop' })
        await Promise.race([finished, new Promise<void>((r) => setTimeout(r, STOP_BUDGET_MS))])
      }
      this.xform?.terminate()
      this.sink?.terminate()
      this.xform = null
      this.sink = null
    })()
    return this.stopping
  }
}
